import type { ReactNode } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@alaffia-technology-solutions/ds";
import { cn } from "@alaffia-technology-solutions/tailwind-utils";

import { Check, Circle, ListChecks, X } from "lucide-react";

import { ExpandablePanel } from "../ExpandablePanel";
import { List } from "./List";

interface TaskHeaderProps {
  title?: string;
}

const TaskListHeader = ({ title = "Task Library" }: TaskHeaderProps) => (
  <ExpandablePanel.Header position="left">
    <span className="af-flex af-w-full af-items-center af-justify-between">
      <span className="af-flex af-h-12 af-items-center af-gap-2 af-whitespace-nowrap af-py-2 af-pl-2 af-text-base">
        <ListChecks className="af-size-4" />
        {title}
      </span>
    </span>
  </ExpandablePanel.Header>
);

type TaskValidationStatus = "accepted";
interface Task {
  isActive: boolean;
  validationStatus: TaskValidationStatus | null;
  onClick: () => void;
  name: string;
  disabled?: boolean;
  isExpanded?: boolean;
}

const getTaskListItemIcon = (
  isActive: boolean,
  validationStatus: TaskValidationStatus | null,
) => {
  if (validationStatus === "accepted") {
    return <Check className="af-text-primary" />;
  }
  if (validationStatus === "rejected") {
    return <X className="af-text-muted-foreground" />;
  }

  return (
    <Circle
      className={cn("af-text-foreground/40", {
        "af-text-primary": isActive,
      })}
    />
  );
};

type TaskListItemProps = Task;

const TaskListItem = ({
  isActive,
  validationStatus,
  name,
  onClick,
  disabled,
  isExpanded,
}: TaskListItemProps) => (
  <Tooltip>
    <TooltipTrigger className="af-p-0">
      <List.Item
        isActive={isActive}
        onClick={onClick}
        disabled={disabled}
        className={cn("af-flex af-items-center af-gap-2 af-truncate af-p-2", {
          "af-font-medium af-text-foreground": validationStatus === "accepted",
        })}
      >
        <List.Icon>{getTaskListItemIcon(isActive, validationStatus)}</List.Icon>
        {isExpanded && <span className="af-truncate">{name}</span>}
      </List.Item>
    </TooltipTrigger>
    <TooltipContent>{name}</TooltipContent>
  </Tooltip>
);

interface TaskListProps {
  children: ReactNode;
  expandLabel?: string;
  header?: ReactNode;
  isExpanded: boolean;
  onCollapse: () => void;
  onExpand: () => void;
  title?: string;
}

const TaskListRoot = ({
  children,
  expandLabel = "Expand tasks list",
  header = <TaskListHeader />,
  isExpanded,
  onCollapse,
  onExpand,
}: TaskListProps) => (
  <ExpandablePanel
    className="af-h-full"
    collapse={onCollapse}
    collapsedContent={
      <ExpandablePanel.CollapsedContents
        position="left"
        actions={<></>}
        expandLabel={expandLabel}
      >
        {children}
      </ExpandablePanel.CollapsedContents>
    }
    expandedContent={<List header={header}>{children}</List>}
    expand={onExpand}
    isExpanded={isExpanded}
  />
);

export const TaskList = Object.assign(TaskListRoot, {
  Header: TaskListHeader,
  Item: TaskListItem,
});
